<template>
  <div class="fts-list-view-2">
    <div class="list-view__container inistate-scroller" ref="list-view-container">
      <template v-if="listViewLoaded">
        <div class="lv-design__header" v-if="isMiniStudio">
          <div class="lv-design__module-emoji">
            <fts-emoji :append-to-body="true" :toggle-size="28" v-model="designModuleEmoji" />
          </div>

          <div class="lv-design__header-wrapper">
            <div class="lv-design__header-info">
              <fts-contenteditable :search="true" v-model="designModuleName" placeholder="Name this module"
                class="lv-design__module-name" @input="$v.designModuleData.name.$touch()"
                @input:search="designSetModuleEmoji" />

              <fts-contenteditable v-model="designModuleDescription" placeholder="Give this page a description"
                class="lv-design__module-description" />
            </div>

            <div class="lv-design__error-message" v-if="isDesignModuleNameError">
              {{ isDesignModuleNameError }}
            </div>
          </div>
        </div>

        <template v-else>
          <div class="list-view__title">
            <fts-label-group :item="viewTitle" emoji class="list-view__title-wrapper"></fts-label-group>
          </div>
        </template>

        <div class="list-view__content" >
          <div class="list-view__error" v-if="listingError">
            <div class="list-view__error-title">Something went wrong</div>
            <div class="list-view__error-message">{{listingError}}</div>
          </div>

          <template v-else>
            <div class="list-view__content-header" :style="contentElWidthStyle">
              <div class="list-view__content-header__left">
                <template v-if="isMiniStudio">
                  <fts-btn @click="openDesignListingDrawer" text text-transform-init
                    color="var(--inistate-text-secondary-color)" style="font-weight: var(--inistate-font-weight-light);">
                    <font-awesome-icon :icon="['fas', 'diagram-project']" /> Views
                  </fts-btn>

                  <fts-btn @click="openDesignWorkflowDrawer('flow')" text text-transform-init
                    color="var(--inistate-text-secondary-color)" style="font-weight: var(--inistate-font-weight-light);">
                    <font-awesome-icon :icon="['fas', 'diagram-project']" data-v-step="workflows"/> Workflows
                  </fts-btn>
                </template>

                <template v-else>
                  <!-- create -->
                  <fts-btn 
                    color="primary" 
                    text-transform-init 
                    box-shadow-none
                    class="fts-font-normal" 
                    @click="createEntry" 
                    v-if="isCreatable"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />New
                  </fts-btn>
                  <!-- activities -->
                  <fts-btn 
                    v-for="activity in activityList" 
                    :key="activity.id"
                    outline 
                    :color="activity.color ? activity.color : 'primary'" 
                    :emoji="activity.emoji"
                    text-transform-init 
                    class="btn-activity" 
                    @click="
                      executeActivity(
                        activity,
                        resultSet.list.filter((x) => x.selected)
                      )
                    "
                  >
                    <span class="btn-activity-label">{{ activity.name }}
                      <fts-test-badge v-if="activity.development" :stage="activity.development"></fts-test-badge>
                    </span>
                  </fts-btn>
                  <!-- documents -->
                  <fts-btn outline color="ternary-green" text-transform-init class="btn-activity"
                    v-for="action in documentList" :key="action.id" @click="
                      executeDocument(
                        action,
                        resultSet.list.filter((x) => x.selected)
                      )
                      ">
                    <font-awesome-icon :icon="['far', 'file-pdf']" class="btn-activity-icon fts-file-pdf"
                      v-if="action.typeId === 'pdf'" />
                    <font-awesome-icon :icon="['far', 'file-alt']" class="btn-activity-icon fts-file-excel"
                      v-else-if="action.typeId === 'spreadsheet'" />
                    <font-awesome-icon :icon="['far', 'file-code']" class="btn-activity-icon fts-file-html"
                      v-else-if="action.typeId === 'html'" />
                    <font-awesome-icon :icon="['far', 'file']" class="btn-activity-icon fts-file-default" v-else />
                    <span class="btn-activity-label">{{ action.name }}
                      <fts-test-badge v-if="action.development" :stage="action.development"></fts-test-badge>
                    </span>
                  </fts-btn>
                  <!-- actions -->
                  <fts-btn outline :color="action.color ? action.color : 'ternary-blue'" :emoji="action.emoji"
                    text-transform-init class="btn-activity" v-for="action in actionList" :key="action.id" @click="
                      executeAction(
                        action,
                        resultSet.list.filter((x) => x.selected)
                      )
                      ">
                    <span :class="{ 'btn-activity-label': !action.color }">
                      {{ action.name }}
                    </span>
                  </fts-btn>
                  <!-- standard -->
                  <fts-btn 
                    v-for="activity in bulkActivities" 
                    :key="activity.id" 
                    outline 
                    text-transform-init 
                    class="btn-activity"
                    :color="activity.id === 'delete' ? 'danger' : 'primary'" 
                    @click="
                      executeActivity(
                        activity,
                        resultSet.list.filter((x) => x.selected)
                      )
                    "
                  >
                    <i :class="activity.icon"></i> {{ activity.name }}
                  </fts-btn>
                   <fts-tippy
                    triggerClass="sidenav-menu-outer"
                    content="Create new activity"
                    :options="{ placement: 'right' }"
                    class="sidenav-menu"
                  >
                    <div
                    class="activities-design-toggle-create"
                      v-if="isDesignMode"
                      @click="
                        openDesignWorkflowDrawer('action','create')
                      "
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" fixed-width/>
                    </div>
                  </fts-tippy>
                </template>
              </div>

              <div class="list-view__content-header__right">
                <fts-sort 
                  :modules="workspaceModules" 
                  :fields="columns" 
                  v-model="sort" 
                  :total-items-exceed="isTotalItemsExceed" 
                  @closed="retrieveItems" 
                  v-if="isCapableOf(SYSTEM_CAPABILITY.Sort)" 
                />
              </div>
            </div>

            <div class="list-view__content-divider"></div>

            <fts-query-designer 
              :modules="workspaceModules" 
              :states="states" 
              :information-source="filterColumns" 
              :filter="filters" 
              mode="view" 
              class="list-view__content-filter" 
              :style="contentElWidthStyle" 
              v-if="!isMiniStudio && isCapableOf(SYSTEM_CAPABILITY.Query)" 
            />
            <table class="fts-data-table">
              <thead class="data-table__head">
                <draggable 
                  :disabled="!isDesignMode"
                  v-model="sortableInformation" 
                  v-bind="dragOptions"  
                  @end="onInformationSortEnd"
                  class="draggable data-table__row"
                  item-key="id"
                  tag="tr"
                  data-v-step="all-tasks"
                >
                  <template
                    v-for="(column, colIndex) in sortableInformation" 
                  >
                    <th
                      :key="`design-${column.id}`" 
                      class="data-table__cell data-table__cell--header data-table__cell--design"
                      :class="{
                        'data-table__cell--primary-cell': column.primaryField,
                      }"
                      v-if="isDesignMode && (!column.system || column.id == 'state')"
                    >
                      <div class="data-table__label-group">
                         <span class="label-text"> {{ column.displayName }}</span>
                          <div class="actions-group">
                            <font-awesome-icon class="actions-group-action" :icon="['fas', 'ellipsis-vertical']" @click.prevent="( column.id == 'state')? openDesignWorkflowDrawer('state') :openColumnDrawer(column)"  />
                            <fts-form-design-shortcut 
                              ref="ftsAddColumnShortcut" 
                              :modules="designWorkspaceModules" 
                              :options="designModuleInformation" 
                              :selected-options="[]" 
                              suggestion 
                              placement="bottom-start"
                              @option:selected="evt => designAddField(evt)" 
                              @option:create="evt => designCreateField(evt, colIndex + 1)"
                              v-if="isDesignMode"
                            > 
                              <font-awesome-icon class="actions-group-action"  :icon="['fas', 'plus']"  />
                            </fts-form-design-shortcut>
                          </div>
                      </div>
                    </th>
                    <th
                      :key="column.id"
                      class="data-table__cell data-table__cell--header" 
                      :class="{
                        'data-table__cell--primary-cell': column.primaryField 
                      }"
                      v-else
                    >
                      <div class="data-table__label-group">
                        <div>{{ column.displayName }}</div>
                      </div> 
                    </th>
                  </template>
                </draggable>
              </thead>

              <tbody class="data-table__body">
                <tr 
                  v-for="item in resultSet.list" 
                  :key="item.id" 
                  class="data-table__row" 
                  :class="{ 'data-table__row--selected': item.selected }" 
                  @click="selectRow(item)"
                >
                  <td 
                    v-for="column in sortableInformation" 
                    :key="column.id" 
                    class="data-table__cell" 
                    :class="{ 'data-table__cell--primary-cell': column.primaryField }"
                  >
                    <template v-if="column.system">
                      <!-- document id -->
                      <div v-if="column.fieldName === 'documentId'">
                        {{ item.documentId }}
                      </div>
                      <!-- state -->
                      <fts-state :state="item.state" :states="states" class="data-table__status"
                        v-else-if="column.fieldName === 'state'" />
                      <!-- system date -->
                      <div v-else-if="column.fieldName === 'date'">
                        {{ moment(item.date).format("ll") }}
                      </div>
                      <!-- created date -->
                      <div v-else-if="column.fieldName === 'createdDate'">
                        {{ moment(item.audit.createdDate).format("llll") }}
                      </div>
                      <!-- created By -->
                      <div class="fts-audit" v-else-if="column.fieldName === 'createdBy'">
                        <fts-profile :userName="item.audit.createdBy" class="audit-profile" />
                        <fts-tippy class="audit-date-wrapper" :content="moment(item.audit.createdDate).format('llll')">
                          <div slot="trigger" class="audit-date">
                            {{ momentRelativeTime(item.audit.createdDate) }}
                          </div>
                        </fts-tippy>
                      </div>
                      <!-- updated date -->
                      <div v-else-if="column.fieldName === 'lastChangeDate'">
                        {{ moment(item.audit.updatedDate).format("llll") }}
                      </div>
                      <!-- updated by -->
                      <div class="fts-audit" v-else-if="column.fieldName === 'lastChangeBy'">
                        <fts-profile :userName="item.audit.updatedBy" class="audit-profile" />
                        <fts-tippy class="audit-date-wrapper" :content="moment(item.audit.updatedDate).format('llll')">
                          <div slot="trigger" class="audit-date">
                            {{ momentRelativeTime(item.audit.updatedDate) }}
                          </div>
                        </fts-tippy>
                      </div>
                      <!-- assign to -->
                      <fts-assign-to 
                        :module-id="moduleId" 
                        :form-data="item" 
                        :view-mode="!isAssignRight" 
                        :comment-section="isCommentRight" 
                        class="stopPropagation" 
                        @refresh="refresh"
                        v-else-if="column.fieldName === 'assignTo'" 
                      />
                      <!-- username -->
                      <div v-else-if="column.fieldName === 'username'">
                        <fts-profile 
                          :user-name="item.extension.username" 
                          profile-group
                          v-if="item.extension && item.extension.username" 
                        />
                      </div>
                    </template>

                    <fts-list-view-display 
                      :form-data="item.extension || item" 
                      :item="column" 
                      :vector-id="moduleId" 
                      enable-text-alignment 
                      v-else 
                    />

                    <template v-if="column.primaryField">
                      <!-- shortcut -->
                      <div class="data-table__cell-shortcut stopPropagation" v-if="isQuickViewRight || isViewRight">
                        <fts-btn 
                          sm 
                          text 
                          text-transform-init 
                          box-shadow-none
                          @click.native.prevent="openViewForm(item)"
                        >
                          <i class="fts-icon-file"></i> View
                        </fts-btn>
                      </div>
                    </template>
                  </td>

                  <td class="data-table__cell data-table__cell-action">
                    <fts-standard-menu 
                      :item="item" 
                      :standard-actions="standardActivities" 
                      :module-id="moduleId"
                      :list-id="listingId" 
                      class="stopPropagation"
                      @executed="evt => executeActivity(evt, formData.filter(x => x.selected))" 
                      v-if="!bulk && item.selected"
                    ></fts-standard-menu>
                  </td>
                </tr>
              </tbody>
            </table>

            <fts-pagination 
              :pagination="isCapableOf(SYSTEM_CAPABILITY.Pagination)" 
              :total-items="totalItems" 
              :total-items-limit="totalItemslimit" 
              v-model="pageSize" 
              class="list-view__content-pagination" 
              :style="contentElWidthStyle" 
              @pageChanged="pageChanged" 
              @page-size-changed="setPageSizePreference" 
              v-if="isCapableOf(SYSTEM_CAPABILITY.Pagination) || isCapableOf(SYSTEM_CAPABILITY.NextPage)"
            ></fts-pagination>
          </template>
        </div>
      </template>
    </div>

    <fts-list-view-form></fts-list-view-form>
    <fts-spinner full-screen boundary="parent" v-if="loading" />
    <fts-action-page-modal ref="ftsActionPageModal" />
    <fts-document-modal ref="ftsDocumentModal" />
    <fts-document-form-modal ref="ftsDocumentFormModal" />
    <fts-design-workflow-drawer ref="ftsDesignWorkflowDrawer" :workflow-data="workflowData" />
    <fts-design-listing-drawer ref="ftsDesignListingDrawer" :listing-data="listingData" />
    <fts-information-design-modal client-mode ref="ftsInformationDesignModal" />
    <fts-information-delete-confirmation ref="ftsInformationDeleteConfirmation" />
    <fts-information-design-drawer ref="ftsInformationDesignDrawer" />
  </div>
</template>

<script>
import Vue from "Vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import moment from 'moment';
import changesets from 'diff-json';
import draggable from 'vuedraggable';

import commons from '../../commons/commons';
import { SYSTEM_CAPABILITY, isCapableOf, Listing_Max_Total_Entries } from '../../commons/flags';
import queryLogic from '../../commons/queryLogic';
import { authorizationStore } from '../../store/authorizationStore';
import { clientStore } from '../../store/clientStore';
import { classificationStore } from '../../store/classificationStore';
import { store as canvasDesignStore } from "../../store/canvasDesignStore";
import LocalStorageService from '../../store/LocalStorageService';
import listing from '../../commons/listing';
import designValidator from '../../commons/designValidator';
import canvasDesign from '../../commons/canvasDesign';

import FTSBtn from '../FTSBtn';
import FTSLabelGroup from '../FTSLabelGroup';
import FTSListViewDisplay from '../FTSListViewDisplay';
import FTSPagination from '../FTSPagination';
import FTSProfile from '../FTSProfile';
import FTSQueryDesigner from '../../design/FTSQueryDesigner';
import FTSSpinner from '../FTSSpinner';
import FTSState from '../FTSState';
import FTSDocumentModal from '../FTSDocumentModal';
import FTSDocumentFormModal from '../FTSDocumentFormModal';
import FTSTestBadge from '../FTSTestBadge';
import FTSActionPageModal from '../FTSActionPageModal';
import FTSTippy from '../FTSTippy';
import FTSStandardMenu from '../FTSStandardMenu';
import FTSListViewForm from './FTSListViewForm';
import FTSAssignTo from '../FTSAssignTo';
import FTSSort from '../FTSSort';
import FTSContenteditable from '../FTSContenteditable';
import FTSEmoji from '../FTSEmoji';
import FTSDesignWorkflowDrawer from '../../design/FTSDesignWorkflowDrawer';
import FTSDesignListingDrawer from '../../design/FTSDesignListingDrawer';
import FTSFormDesignShortcut from '../../design/FTSFormDesignShortcut';
import FTSInformationDesignModal from '../../design/FTSInformationDesignModal';
import FTSInformationDeleteConfirmation from '../../design/FTSInformationDeleteConfirmation';
import FTSDropdownSelect from '../FTSDropdownSelect';
import FTSInformationDesignDrawer from '../../design/FTSInformationDesignDrawer';

const listViewModes = [
  { id: "list", code: "l" },
  { id: "card", code: "c" },
  { id: "board", code: "b" },
  { id: "calendar", code: "d" },
  { id: "detail", code: "dv" }
];
const BULK_MAX_COUNT = 50;
const BULK_EXCEED_ERROR = `Selected items exceeded. Maximum only allows to select up to ${BULK_MAX_COUNT}.`;

export default {
  props: {
    client: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    draggable,
    'fts-btn': FTSBtn,
    'fts-label-group': FTSLabelGroup,
    'fts-list-view-display': FTSListViewDisplay,
    'fts-pagination': FTSPagination,
    'fts-profile': FTSProfile,
    'fts-query-designer': FTSQueryDesigner,
    'fts-spinner': FTSSpinner,
    'fts-state': FTSState,
    'fts-document-modal': FTSDocumentModal,
    'fts-document-form-modal': FTSDocumentFormModal,
    'fts-test-badge': FTSTestBadge,
    'fts-action-page-modal': FTSActionPageModal,
    'fts-tippy': FTSTippy,
    'fts-standard-menu': FTSStandardMenu,
    'fts-list-view-form': FTSListViewForm,
    'fts-assign-to': FTSAssignTo,
    'fts-sort': FTSSort,
    'fts-contenteditable': FTSContenteditable,
    'fts-emoji': FTSEmoji,
    'fts-design-workflow-drawer': FTSDesignWorkflowDrawer,
    'fts-design-listing-drawer': FTSDesignListingDrawer,
    'fts-form-design-shortcut': FTSFormDesignShortcut,
    'fts-information-design-modal': FTSInformationDesignModal,
    'fts-information-delete-confirmation': FTSInformationDeleteConfirmation,
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-information-design-drawer': FTSInformationDesignDrawer,
  },
  data: () => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    resizeObserver: null,
    autoSave: null,
    contentElWidthStyle: null,
    mode: "list",
    header: {},
    moduleId: null,
    listingId: null,
    listingError: null,
    columns: [],
    preferences: [],
    originalDefaultFilters: null,
    filters: null,
    skipFiltersWatch: false,
    sort: null,
    resultSet: { list: [] },
    pageSize: 20,
    page: 0,
    loading: false,
    listViewLoaded: false,
    autoRefreshInterval: null,
    bulk: false,
    dragOptions: {
      animation: 200,
      ghostClass: 'ghost'
    },
    sortableInformation: [],
    designReady: false,
  }),
  validations() {
    let designModuleData = {};
    designModuleData = designValidator.moduleDesign(canvasDesignStore.getters.workspaceModules)
    return { designModuleData }
  },
  methods: {
    moment,
    momentRelativeTime(date) {
      return commons.momentRelativeTime(date);
    },
    isCapableOf(type) {
      return isCapableOf(type, this.workspaceModules, this.moduleId);
    },
    isRightOf(type, list) {
      return list.indexOf(type) > -1;
    },
    entryURL(id) {
      return commons.clientPathUrl({
        moduleId: this.moduleId,
        listId: this.listingId,
        id: id,
      });
    },
    resetScroll() {

    },
    async loadWorkspaceList(payload = {}) {
      return await clientStore.dispatch("loadWorkspaceList", {
        moduleId: this.moduleId,
        listingId: this.listingId,
        withHeader: (payload && payload.withHeader) || false,
        currentPage: this.page,
        pageSize: this.pageSize,
        filters: (payload && payload.filters) || null,
        sorts: this.sort ? this.sort : null,
        contextId: clientStore.state.workspaceContext != null ?
          clientStore.state.workspaceContext.id :
          null
      });
    },
    setFiltersInternally(filters) {
      this.debouncedFilters.cancel();
      this.skipFiltersWatch = true;
      this.filters = filters;
      this.$nextTick(() => {
        this.skipFiltersWatch = false;
      });
    },
    setLocalStoragePreferenceFilters(preference, filters) {
      let savedPreference = { ...preference };

      if (
        filters &&
        Array.isArray(filters.items) &&
        filters.items.length > 0
      ) {
        savedPreference.filters = filters;
      } else {
        delete savedPreference.filters;
      }

      LocalStorageService.setModuleListingPreference(
        this.moduleId,
        this.listingId,
        savedPreference
      );
    },
    clearLocalStoragePreferenceFilters() {
      let preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
      if (preference) {
        delete preference.filters;

        LocalStorageService.setModuleListingPreference(
          this.moduleId,
          this.listingId,
          preference
        );
      }

      let defaultFilters = { "items": [] };
      if (this.originalDefaultFilters) {
        defaultFilters = this.originalDefaultFilters;
      }

      this.setFiltersInternally(defaultFilters);
      this.refresh();
    },
    setInitialFilters() {
      let preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);

      if (preference && preference.filters) {
        if (
          Array.isArray(preference.filters.items) &&
          preference.filters.items.length
        ) {
          this.setFiltersInternally(preference.filters);
        } else {
          this.setLocalStoragePreferenceFilters(preference, null);
        }
      }
    },
    composeFilterColumns(columns) {
      let filterColumns = _.cloneDeep(columns);
      // collaboration
      filterColumns.splice(1, 0, {
        ...queryLogic.querySelectionPrefix.find(x => x.id === "collaboration")
      });
      // assign to
      const assignToIndex = filterColumns.findIndex(x => x.id === "assignTo");
      if (assignToIndex > -1) {
        const isDueExisted = filterColumns.some(x => x.id === "due");
        if (!isDueExisted) {
          filterColumns.splice(assignToIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "due")
          });
        }
      }
      // created date
      const createdByIndex = filterColumns.findIndex(x => x.id === "createdBy");
      if (createdByIndex > -1) {
        const isCreatedDateExisted = filterColumns.some(x => x.id === "createdDate");
        if (!isCreatedDateExisted) {
          filterColumns.splice(createdByIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "createdDate")
          });
        }
      }
      // last change date
      const lastChangeByIndex = filterColumns.findIndex(x => x.id === "lastChangeBy");
      if (lastChangeByIndex > -1) {
        const isLastChangeDateExisted = filterColumns.some(x => x.id === "lastChangeDate");
        if (!isLastChangeDateExisted) {
          filterColumns.splice(lastChangeByIndex + 1, 0, {
            ...queryLogic.querySelectionPrefix.find(x => x.id === "lastChangeDate")
          });
        }
      }

      return filterColumns.filter(column => {
        if (column.system) return true;

        const informationType = commons.informationTypes.find(x => x.id === column.type);
        return Boolean(informationType && informationType.searchable);
      });
    },
    setPageSizePreference(value) {
      const preference = {
        ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId),
        pageSize: value
      };
      LocalStorageService.setModuleListingPreference(this.moduleId, this.listingId, preference);
    },
    async refresh(payload = {}) {
      const withHeader = Boolean(payload && payload.withHeader);
      const withPreferenceFilters = Boolean(payload && payload.withPreferenceFilters);
      const preserveSelectedEntries = Boolean(payload && payload.preserveSelectedEntries);

      try {
        let res = null;

        if (withHeader) {
          let preference = null;
          let isPreferenceFilters = false;

          if (withPreferenceFilters) {
            preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
            isPreferenceFilters = Boolean(preference && preference.filters);
          }

          try {
            res = await this.loadWorkspaceList({ withHeader: true, filters: this.filters });
          } catch (err) {
            if (isPreferenceFilters) {
              isPreferenceFilters = false;
              this.setLocalStoragePreferenceFilters(preference, null);

              res = await this.loadWorkspaceList({ withHeader: true });
            } else {
              throw err;
            }
          }

          const resultData = _.cloneDeep(res.data);

          if (isPreferenceFilters) {
            const filterColumns = this.composeFilterColumns(resultData.header.columns);
            let clonedFilters = _.cloneDeep(this.filters);
            queryLogic.updateQueryFilters(filterColumns, clonedFilters);
            const diff = changesets.diff(clonedFilters, this.filters);

            if (diff && diff.length) {
              if (
                !Array.isArray(clonedFilters.items) ||
                !clonedFilters.items.length
              ) {
                clonedFilters = null;
              }

              try {
                res = await this.loadWorkspaceList({ filters: clonedFilters });
              } catch (err) {
                clonedFilters = null;
                res = await this.loadWorkspaceList();
              }

              if (clonedFilters) {
                this.setFiltersInternally(clonedFilters);
                this.setLocalStoragePreferenceFilters(preference, clonedFilters);
              } else {
                this.setFiltersInternally({
                  items: [
                    queryLogic.getDefaultQueryFilter(filterColumns, { workspaceBeta: clientStore.getters.workspaceBeta })
                  ]
                });

                isPreferenceFilters = false;
                this.setLocalStoragePreferenceFilters(preference, null);
              }
            }
          }

          this.header = resultData.header;
          this.columns = resultData.header.columns;
          this.moduleImport = resultData.import;

          // "everything" listing should always have state column (excluded from state add/remove rule)
          if (this.listingId === 'everything') {
            const hasStateColumn = this.columns.some(col => col.id === 'state');
            if (!hasStateColumn) {
              const stateColumn = {
                id: 'state',
                displayName: 'State',
                fieldName: 'state',
                type: 'state',
                system: true
              };
              const actionIndex = this.columns.findIndex(col => col.id === 'action');
              if (actionIndex > -1) {
                this.columns.splice(actionIndex, 0, stateColumn);
              } else {
                this.columns.push(stateColumn);
              }
            }
          }

          if (
            this.header.defaultFilters &&
            this.header.defaultFilters.items &&
            this.header.defaultFilters.items.length
          ) {
            const { filters, originalDefaultFilters, defaultFilterChanged } = listing.setDefaultFilters(
              this.header.defaultFilters,
              this.originalDefaultFilters,
              this.filters,
              this.columns
            );

            this.originalDefaultFilters = originalDefaultFilters;

            if (!isPreferenceFilters) {
              this.setFiltersInternally(filters);

              if (this.schemaLoad && defaultFilterChanged) {
                res = await this.loadWorkspaceList({ filters: filters });
              }
            }
          } else if (!this.schemaLoad && !isPreferenceFilters) {
            this.setFiltersInternally({
              items: [
                queryLogic.getDefaultQueryFilter(this.columns, { workspaceBeta: clientStore.getters.workspaceBeta })
              ]
            });
            this.originalDefaultFilters = null;
          }
          // default sort
          if (this.header.defaultSort) {
            this.sort = _.cloneDeep(this.header.defaultSort);
          }
          // system capability
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Write)) {
            this.header.standards = this.header.standards.filter(x =>
              ["create", "edit", "changeStatus", "duplicate", "assign"].indexOf(x) === -1
            );
          }
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Delete)) {
            this.header.standards = this.header.standards.filter(x => x !== "delete");
          }
          if (!this.isCapableOf(SYSTEM_CAPABILITY.Read)) {
            this.header.standards = this.header.standards.filter(x => ["view", "quickView"].indexOf(x) === -1);
          }
          // preferences
          this.originalPreferences = this.header.preferences;
          this.preferences = this.header.preferences;
          if (this.preferences && this.preferences.length) {
            const diffColumns = _.differenceBy(this.columns, this.preferences, 'id').map(x => ({ id: x.id }));
            const diffPreferences = _.differenceBy(this.preferences, this.columns, 'id');
            if (diffColumns.length) {
              this.preferences = this.preferences.concat(diffColumns);
            }
            if (diffPreferences.length) {
              diffPreferences.forEach(x => {
                this.preferences.splice(this.preferences.indexOf(x), 1);
              });
            }
          }
          // auto refresh
          if (this.autoRefreshInterval) {
            clearInterval(this.autoRefreshInterval);
            this.autoRefreshInterval = null;
          }

          if (this.header && this.header.autoUpdate && !this._isBeingDestroyed && !this._isDestroyed) {
            this.autoRefreshInterval = setInterval(async () => {
              if (!this.loading) {
                const cloneSelectedIds = this.formData.filter((x) => x.selected).map((x) => x.id);
                await this.retrieveItems();
                if (cloneSelectedIds) {
                  cloneSelectedIds.forEach((x) => {
                    let res = this.formData.find((y) => y.id === x);
                    res && this.$set(res, "selected", true);
                  });
                }
              }
            }, 5000);
          }

          this.schemaLoad = true;
        } else {
          res = await this.loadWorkspaceList({ filters: this.filters });
        }

        let resultSet = res.data.data;

        if (
          preserveSelectedEntries &&
          this.resultSet &&
          this.resultSet.list &&
          this.resultSet.list.length
        ) {
          const selectedIds = {};
          for (let i = 0; i < this.resultSet.list.length; i++) {
            const item = this.resultSet.list[i];
            if (item.selected) selectedIds[item.id] = true;
          }

          for (let i = 0; i < resultSet.list.length; i++) {
            if (selectedIds[resultSet.list[i].id]) {
              resultSet.list[i].selected = true;
            }
          }
        }

        this.resultSet = resultSet;
        this.listingError = null;
      } catch (err) {
        let errorMessage = "Unable to load the list. Please try again.";

        if (
          err.response && 
          err.response.data && 
          err.response.data.message
        ) {
          errorMessage = err.response.data.message;
        }

        this.listingError = errorMessage;
        console.error(err);
      }
    },
    async initialSchema() {
      this.setInitialFilters();
      await this.refresh({ withHeader: true, withPreferenceFilters: true });
    },
    setListViewMode(mode, { moduleId, listingId } = {}) {
      this.mode = listViewModes.some((x) => x.id === mode) ? mode : "list";
      if (moduleId) {
        let preference = {
          ...LocalStorageService.getModuleListingPreference(moduleId, listingId),
          type: this.mode
        }

        // remove detailView's code

        LocalStorageService.setModuleListingPreference(moduleId, listingId, preference);
      }
      this.resetScroll();
    },
    initListViewMode({ code, moduleId, listingId } = {}) {
      let mode = null;
      if (this.client && code) {
        const listViewMode = listViewModes.find((x) => x.code === code.toLowerCase());
        Boolean(listViewMode && listViewMode.id) && (mode = listViewMode.id);
      }
      if (!mode) {
        const listViewPreference = LocalStorageService.getModuleListingPreference(moduleId, listingId);
        Boolean(listViewPreference && listViewPreference.type) && (mode = listViewPreference.type);
      }
      this.setListViewMode(mode, { moduleId, listingId });
    },
    async retrieveItems(payload) {
      this.loading = true;
      await this.refresh(payload);
      this.loading = false;
    },
    async pageChanged(page) {
      this.page = page;
      await this.retrieveItems();
      this.resetScroll();
    },
    setSort(item, order) {
      const sort = `${item.id}:${order}`;
      if (sort == this.currentSort) {
        this.sort = null;
      } else {
        this.sort = [{
          fieldId: item.id,
          direction: order
        }];
      }

      this.retrieveItems();
    },
    executeActivity(activity, list, fullView) {
      if (list.length > BULK_MAX_COUNT) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }

      window.eventBus.$emit("list-view-form:open", {
        entries: list,
        moduleId: this.moduleId,
        listingId: this.listingId,
        activityId: activity.id
      });
    },
    executeAction(action, items) {
      commons.executeEntryCustom({
        action: action,
        items: items,
        modal: this.$refs.ftsActionPageModal
      });
    },
    async executeDocument(action, items) {
      if (items.length > BULK_MAX_COUNT) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }

      let vm = this;
      if (action.type == "template") {
        let template = {
          templateId: action.id,
          entryId: items[0].id,
        };

        this.$refs.ftsDocumentModal.show(template);
      } else if (action.hasForm) {
        let items = this.resultSet.list.filter((x) => x.selected);
        await this.$refs.ftsDocumentFormModal.show({
          moduleId: this.moduleId,
          listingId: this.listingId,
          document: action,
          items: items,
          filters: this.filters
        });
      } else {
        let parameters = {
          document: action,
          items: items,
          moduleId: this.moduleId,
          listingId: this.listingId,
          filters: this.filters,
        };

        if (action.dataSourceId == "logicService") {
          if (!parameters.inputs) {
            parameters.inputs = {};
          }

          let items = this.resultSet.list.filter((x) => x.selected);
          if (items != null && items.length > 0) {
            parameters.inputs.id = items[0].id;
            parameters.inputs.locusIds = items.map(x => x.id);
          }
        }

        window.eventBus.$emit("showPDFDocument", parameters);
      }
    },
    createEntry(fullView) {
      this.executeActivity({ id: "create" }, [], fullView);
    },
    openForm() {
      if (
        this.$route.query.id || 
        (this.$route.query.a === "create" && this.isCreatable)
      ) {
        let entryId = null;
        let moduleId = this.moduleId;
        let listingId = this.$route.query.l;

        if (this.$route.query.id) {
          const queryDecodeids = commons.queryDecodeids(this.$route.query.id);
          if (queryDecodeids && queryDecodeids.entryId) {
            entryId = queryDecodeids.entryId;
          }
          if (queryDecodeids && queryDecodeids.moduleId) {
            moduleId = queryDecodeids.moduleId;
            if (this.moduleId != moduleId) {
              listingId = null;
            }
          }
        }

        window.eventBus.$emit("list-view-form:open", {
          id: entryId,
          moduleId: moduleId,
          listingId: listingId,
          activityId: this.$route.query.a
        });
      }
    },
    setRow(row, value) {
      if (value === true && !this.bulk) {
        this.formData.forEach(x => {
          this.$set(x, "selected", false);
        });
      }

      this.$set(row, "selected", value);
    },
    selectRow(row) {
      if (commons.elementStopPropagation())
        return;

      const value = !Boolean(row && row.selected);
      this.setRow(row, value);
    
      if (value === true) {
        let activityId = null;
        let entryId = null;

        if (this.$route.query) {
          activityId = this.$route.query.a && this.$route.query.a.toLowerCase();
          entryId = this.$route.query.id;
        }

        let isFormOpen = false;

        if (activityId === "create") {
          isFormOpen = true;
        } else if (activityId !== "create" && entryId) {
          isFormOpen = true;
        }

        if (isFormOpen) {
          this.executeActivity({ id: "quickView" }, [row]);
        }
      }
    },
    openViewForm(row) {
      this.executeActivity({ id: "quickView" }, [row]);
      this.setRow(row, true);
    },
    openDesignWorkflowDrawer(initialTab, defaultAction) {
      if (this.$refs.ftsDesignWorkflowDrawer) {
        this.$refs.ftsDesignWorkflowDrawer.show();
        if(initialTab) this.$refs.ftsDesignWorkflowDrawer.activeTab = initialTab;
      }
      if(defaultAction =='create'){
        if(initialTab =='state')
          this.$refs.ftsDesignWorkflowDrawer.createState();
        if(initialTab =='action')
          this.$refs.ftsDesignWorkflowDrawer.createAction();
      }
    },
    openDesignListingDrawer(args) {
      if (!this.$refs.ftsDesignListingDrawer) return;

      this.$refs.ftsDesignListingDrawer.show();
      if (args.listingId) {
        this.$refs.ftsDesignListingDrawer.editListing(args.listingId)
        return;
      };

      if(args.new){
        this.$refs.ftsDesignListingDrawer.createListing();
      }
    },
    onInformationSortEnd() {
      this.dragging = false;
      this.sortableInformation =[...this.sortableInformation];
      // Get new order from the dragged array
      const newOrder = this.sortableInformation.filter((x)=> !x.system); 
      // Clone canvas and update information order
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);

      canvas.information = newOrder; 

      // Update store and auto-save
      canvasDesignStore.dispatch("updateVectorCanvas", canvas);
      this.onDesignChange();

      // Sync table columns to match the new information order
      if (this.columns && this.columns.length) {
        const columnMap = new Map(this.columns.map(col => [col.id, col]));
        const reorderedColumns = [];

        // Reorder columns based on the new information order
        for (const info of newOrder) {
          const col = columnMap.get(info.id);
          if (col) {
            reorderedColumns.push(col);
            columnMap.delete(info.id);
          }
        }

        // Append any remaining columns not in information (e.g., system columns)
        for (const col of columnMap.values()) {
          reorderedColumns.push(col);
        }

        this.columns = this.sortableInformation;
      }
    },
    informationIcon(type) {
      const infoType = commons.informationTypes.find(x => x.id === type);
      return infoType ? infoType.icon : 'fts-icon-text';
    },
    prefillColumnName(column) {
      // After dropdown opens, pre-fill the input with current column name
      this.$nextTick(() => {
        setTimeout(() => {
          const refName = `editColumnShortcut_${column.id}`;
          const shortcutRef = this.$refs[refName];
          const shortcut = Array.isArray(shortcutRef) ? shortcutRef[0] : shortcutRef;
          if (shortcut) {
            // Set the filterKeyword to current column name
            shortcut.filterKeyword = column.displayName || '';
            // Trigger AI search
            if (shortcut.onInput) {
              shortcut.onInput();
            }
            // Select all text in input
            if (shortcut.$refs && shortcut.$refs.filterKeywordInput) {
              shortcut.$refs.filterKeywordInput.select();
            }
          }
        }, 50); // Small delay to ensure dropdown is open
      });
    },
    async openColumnDrawer(column) {
      const res = await this.$refs.ftsInformationDesignDrawer.show(column);

      // Handle delete case
      if (res === true) {
        // Column was deleted via the drawer
        // The delete confirmation already handled the canvas update
        // Just sync local state
        this.sortableInformation = [...this.columns];
        const localColIndex = this.columns.findIndex(x => x.id === column.id);
        if (localColIndex > -1) {
          this.columns.splice(localColIndex, 1);
        }
        this.onDesignChange();
        return;
      }

      if (res && typeof res === 'object') {
        try {
          // Update canvas information with all properties from result
          let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
          const infoIndex = canvas.information.findIndex(x => x.id === res.id);
          if (infoIndex > -1) {
            // Replace the entire information object to capture all type-specific properties
            canvas.information[infoIndex] = res;
          }

          // Update menu columns
          if (!canvas.menus) canvas.menus = [];
          let currentMenu = canvasDesign.getMenu(canvas, this.$route.query.l);
          if (currentMenu && currentMenu.list && currentMenu.list.columns) {
            const colIndex = currentMenu.list.columns.findIndex(x => x.id === res.id);
            if (colIndex > -1) {
              currentMenu.list.columns[colIndex] = {
                ...currentMenu.list.columns[colIndex],
                displayName: res.displayName,
                type: res.type,
              };
            }
          }

          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          this.onDesignChange();

          // Update local columns
          const localColIndex = this.columns.findIndex(x => x.id === res.id);
          if (localColIndex > -1) {
            this.columns[localColIndex] = {
              ...this.columns[localColIndex],
              displayName: res.displayName,
              type: res.type,
            };

            // deep clone to force trigger columns update
            this.columns = [...this.columns];
          }

          // Sync sortableInformation
          this.sortableInformation = [...this.columns];
        } catch (err) {
          console.error("openColumnDrawer error:", err);
          this.$toasted.global.error();
        }
      }
    },
    // design
    async designSetModuleEmoji() {
      if (this.designModuleName) {
        try {
          const res = await canvasDesignStore.dispatch("ai", {
            type: "emoji",
            text: this.designModuleName
          });

          if (res.data) {
            this.designModuleEmoji = res.data;
          }
        } catch (err) { console.error(err); }
      }
    },
    designAddField(item) {
      if (!item) return;

      if(item.type == 'state'){
        this.openDesignWorkflowDrawer('state','create');
        return;
      }

      try {
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);

        // Ensure menus array exists before calling getMenu
        if (!canvas.menus) {
          canvas.menus = [];
        }

        // Add to current menu's columns
        let currentMenu = canvasDesign.getMenu(canvas, this.$route.query.l);
        if (currentMenu) {
          currentMenu.list = currentMenu.list || { columns: [] };
          currentMenu.list.columns = Array.isArray(currentMenu.list.columns) ? currentMenu.list.columns : [];

          // Check if column already exists
          const exists = currentMenu.list.columns.some(col => col.id === item.id);
          if (!exists) {
            currentMenu.list.columns.push(item);

            canvasDesignStore.dispatch("updateVectorCanvas", canvas);
            this.onDesignChange();

            // Add to local columns for immediate UI update
            if (this.columns && Array.isArray(this.columns)) {
              const columnExists = this.columns.some(col => col.id === item.id);
              if (!columnExists) {
                this.columns.push(item);
              }
            }
          }
        }
      } catch (err) {
        console.error("designAddField error:", err);
        this.$toasted.global.error();
      }
    },
    async designCreateField(item, insertAt) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      let res = canvasDesign.initInformation(item);

      // Check if this is an AI suggestion by matching against searchSuggestions
      const shortcut = this.$refs.ftsAddColumnShortcut;
      const isAiSuggestion = shortcut && shortcut.searchSuggestions && shortcut.searchSuggestions.some(
        s => s.value && s.value.id === item.type && s.value.name === item.displayName
      );

      /* check duplicated information name */
      let increaseIndex = 0;
      if(item.displayName){
        while (true) {
          const displayName = res.displayName + (increaseIndex > 0 ? ` ${increaseIndex}` : '');

          const duplicatedIndex = this.columns.findIndex(
            x => x.displayName === displayName
          );

          if (duplicatedIndex === -1) {
            // Unique found → set the name and exit
            res.displayName = displayName;
            break;
          }

          increaseIndex++;
        }
      }

      if(!isAiSuggestion && item && typeof item === 'object' && item.type === undefined){
        console.log('more information');
        // res = await this.$refs.ftsInformationDesignModal.show(res, null, null, { excludeTypes: [] });
        res = await this.$refs.ftsInformationDesignDrawer.show(res);
        if (!res || res === true) return;
      }
      
      /* Delete Case */
      if (res === true) {  
        this.sortableInformation = [...this.columns];
        const localColIndex = this.columns.findIndex(x => x.id === column.id);
        if (localColIndex > -1) {
          this.columns.splice(localColIndex, 1);
        }
        this.onDesignChange();
        return;
      }

      if(!res || typeof res !== 'object') return;
      
      /* Proceed information creation Case */
      try {
        canvasDesign.setInformation(canvas, res);
 
        if (!canvas.menus) {
          canvas.menus = [];
        }

        let currentMenu = canvasDesign.getMenu(canvas, this.$route.query.l);
        if (currentMenu) {
          currentMenu.list = currentMenu.list || { columns: [] };
          currentMenu.list.columns = Array.isArray(currentMenu.list.columns) ? currentMenu.list.columns : [];
          currentMenu.list.columns.push(res);
        }

        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        this.onDesignChange();
 
        if (this.columns && Array.isArray(this.columns)) {
          if(Number.isInteger(insertAt) && insertAt >= 0 && insertAt <= this.columns.length){
            this.columns.splice(insertAt, 0, res);
          }else {
            this.columns.push(res);
          }
        }
 
        this.sortableInformation = [...this.columns];
      } catch (err) {
        console.error(err);
        this.$toasted.global.error();
      }
    },
    onDesignChange() {
      this.$v.designModuleData.$touch()
      let saveData = _.cloneDeep(this.designModuleData);

      if (this.isDesignModuleNameError) {
        return;
      }

      this.autoSave.schedule(saveData);
    },
    // async designSaveNow() {
    //   await this.autoSave.flush();
    // },
    async designExecuteSave(payload) {
      try {
        console.info("designExecuteSave:Starting", payload);
        const res = await canvasDesignStore.dispatch("saveVector", payload);
        console.info("designExecuteSave:Completed", res);

        // Reload workspace to sync sidebar with updated module info
        await clientStore.dispatch("loadWorkspace", {
          id: clientStore.getters.workspaceId,
          forceReload: true,
          preserveMenuState: true
        });

        // Notify views to refresh their schema
        window.eventBus.$emit("listing:schemaUpdated");
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    },
    createAutoSave(saveFunction, delay = 1000) {
      let timer = null;
      let lastPayload = null;
      let isSaving = false;

      const triggerSave = async () => {
        if (!lastPayload) return;
        const payloadToSave = lastPayload;
        lastPayload = null;
        isSaving = true;
        try {
          await saveFunction(payloadToSave);
        } catch (err) {
          console.error('Auto-save error:', err);
        } finally {
          isSaving = false;
        }
      };

      return {
        schedule(payload) {
          lastPayload = payload;
          clearTimeout(timer);
          timer = setTimeout(triggerSave, delay);
        },
        async flush() {
          clearTimeout(timer);
          await triggerSave();
        },
        cancel() {
          clearTimeout(timer);
          timer = null;
          lastPayload = null;
        },
        getStatus() {
          return { isSaving, hasPending: !!lastPayload };
        },
      };
    },
    async onOpenDesigner(args) {
      const timeoutMs = 3000; // 最大等待时间 3 秒
      const intervalMs = 50;

      let waited = 0;

      while (!this.designReady && waited < timeoutMs) {
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        waited += intervalMs;
      }

      if (!this.designReady) {
        console.warn("openDesignListingDrawer timeout: mounted not ready");
        return;
      }

      this.openDesignListingDrawer(args);
    }
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isBeta() {
      return authorizationStore.getters.isBeta || this.workspaceBeta;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceBeta() {
      return clientStore.getters.workspaceBeta;
    },
    workspaceName() {
      return clientStore.getters.workspaceName;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    workspaceUsers() {
      return clientStore.getters.workspaceUsers;
    },
    module() {
      return clientStore.getters.module;
    },
    moduleName() {
      return this.module && this.module.name;
    },
    list() {
      return clientStore.getters.list;
    },
    states() {
      return clientStore.getters.moduleStates;
    },
    standardActivities() {
      return this.header && this.header.standards;
    },
    isCreatable() {
      return this.isRightOf("create", this.standardActivities);
    },
    isQuickViewRight() {
      return this.isRightOf("quickView", this.standardActivities);
    },
    isViewRight() {
      return this.isRightOf("view", this.standardActivities);
    },
    isAssignRight() {
      return this.isRightOf("assign", this.standardActivities);
    },
    isCommentRight() {
      return this.isRightOf("comment", this.standardActivities);
    },
    isIndependentView() {
      return ["calendar", "detail"].includes(this.mode);
    },
    isTotalItemsExceed() {
      return this.totalItems >= this.totalItemslimit;
    },
    viewTitle() {
      if (this.list && this.list.id) {
        return {
          emoji: this.list.emoji,
          name: this.list.name
        }
      }

      if (this.module && this.module.id) {
        return {
          emoji: this.module.emoji,
          name: this.module.name
        }
      }

      return {
        name: "(undefined)"
      }
    },
    getColumns() {
      let columns = _.cloneDeep(this.columns);
      // exclude attachments
      columns = columns.filter(
        (col) => col.type !== 12 && col.type !== 13
      );
      // exclude hidden
      columns = columns.filter((col) =>
        (col.show === null || col.show === undefined || col.show === true) &&
        (col.hide === null || col.hide === undefined || col.hide !== true)
      );
      // set primary field
      if (columns && columns[0]) {
        columns[0].primaryField = true;
      }

      return columns;
    },
    filterColumns() {
      let filterColumns = this.composeFilterColumns(this.columns);
      // query selection suffix
      const querySelectionSuffix = queryLogic.querySelectionSuffix
        .filter(x => x.fieldName === "and" || x.fieldName === "or")
        .map(y => (({ ...y, system: true })));

      filterColumns = filterColumns.concat(querySelectionSuffix);

      return filterColumns;
    },
    totalItems() {
      return this.resultSet && this.resultSet.totalItems;
    },
    totalItemslimit() {
      const limit = this.resultSet && this.resultSet.limit;

      if (limit && commons.isInteger(limit) && limit > 0) {
        return limit;
      }

      return Listing_Max_Total_Entries;
    },
    activityList() {
      if (this.resultSet && this.header && this.header.activities) {
        const selectedItems = this.resultSet.list.filter((x) => x.selected);
        if (this.mode == "list" && selectedItems.length) {
          return this.header.activities.filter((activity) => {
            if (selectedItems.length > 1 && activity.disableBulkActions)
              return false;
            if (activity.fromStates && activity.fromStates.length) {
              return selectedItems.every((item) =>
                activity.fromStates.some((y) => y == item.state.id)
              );
            }
            return true;
          });
        }
      }
      return [];
    },
    actionList() {
      /* custom visibility */
      if (this.resultSet && this.header && this.header.customs) {
        let items = this.resultSet.list.filter((x) => x.selected);
        return this.header.customs.filter((action) => {
          if (Boolean(action.condition)) {
            try {
              return Boolean(eval(action.condition));
            } catch (err) {
              console.error('[Custom:Condition error]', err);
              return false;
            }
          }

          if (this.mode == "list" && items.length) {
            if (items.length > 1) {
              return action.isMultiple;
            } else {
              return true;
            }
          }

          return action.isDefault;
        });
      }
      return [];
    },
    documentList() {
      if (this.resultSet && this.header && this.header.documents) {
        let items = this.resultSet.list.filter((x) => x.selected);
        return this.header.documents.filter((action) => {
          if (this.mode == "list" && items.length) {
            if (items.length > 1) {
              return action.allowSelection;
            } else {
              return true;
            }
          }

          return (
            action.dataSourceId == "collection" ||
            action.dataSourceId == "logicService"
          );
        });
      }
      return [];
    },
    bulkActivities() {
      return [] // to-do
    },
    formData() {
      return this.resultSet.list ? this.resultSet.list : [];
    },
    currentSort() {
      if (Array.isArray(this.sort)) {
        const sort = this.sort[0];
        if (sort) {
          return `${sort.fieldId}:${sort.direction}`;
        }
      }

      return null;
    },
    isMiniStudio() {
      return clientStore.getters.miniStudio;
    },
    isDesignMode() {
      return clientStore.getters.designMode;
    },
    designModuleData() {
      return canvasDesignStore.getters.vector;
    },
    designModuleName: {
      get() {
        return canvasDesignStore.getters.vector && canvasDesignStore.getters.vector.name;
      },
      set(val) {
        canvasDesignStore.dispatch("updateModule", {
          ...canvasDesignStore.getters.vector,
          name: val
        });

        this.onDesignChange();
      }
    },
    designModuleEmoji: {
      get() {
        return canvasDesignStore.getters.vector && canvasDesignStore.getters.vector.emoji;
      },
      set(val) {
        canvasDesignStore.dispatch("updateModule", {
          ...canvasDesignStore.getters.vector,
          emoji: val
        });

        this.onDesignChange();
      }
    },
    designModuleDescription: {
      get() {
        return canvasDesignStore.getters.vector && canvasDesignStore.getters.vector.description;
      },
      set(val) {
        canvasDesignStore.dispatch("updateModule", {
          ...canvasDesignStore.getters.vector,
          description: val
        });

        this.onDesignChange();
      }
    },
    isDesignModuleNameError() {
      return designValidator.moduleDesignError(this.$v.designModuleData, "name");
    },
    designWorkspaceModules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    designModuleInformation() {
      const hasStateColumn = this.columns.some(col => col.id === 'state');
      if(hasStateColumn) return[];
      return [
        {
          'id': 'state',
          'displayName': "State",
          'system': true,
          'fieldName': "state",
          'type': "state",
        },
      ];
    },
    designModuleMenus() {
      return canvasDesignStore.getters.canvasMenus;
    },
    workflowData() {
      return {
        states: canvasDesignStore.getters.canvasStates,
        flows: canvasDesignStore.getters.canvasFlows,
        actions: canvasDesignStore.getters.canvasActivities,
      };
    },
    listingData() {
      return {
        listing: canvasDesignStore.getters.canvasMenus,
        vectorName: canvasDesignStore.getters.vector.name,
      };
    }
  },
  watch: {
    filters: {
      deep: true,
      handler(val) {
        this.debouncedFilters.cancel();

        if (this.skipFiltersWatch) return;

        this.debouncedFilters(val);
      }
    },
    columns: {
      immediate: true,
      handler(val) {
        if (val && Array.isArray(val)) {
          this.sortableInformation = [...val.find((x)=>!x.system)];
          this.sortableInformation = [...val];
        }
      }
    }
  },
  created() {
    this.refreshSchemaHandler = () => {
      // "everything" listing should not be affected by schema updates (state changes)
      if (!this.isIndependentView && this.listingId !== 'everything') {
        this.refresh({ withHeader: true, withPreferenceFilters: true });
      }
    }

    this.refreshListHandler = (payload = {}) => {
      if (!this.isIndependentView && (!payload.moduleId || payload.moduleId == this.moduleId)) {
        let option = {};
        if (payload.preserveSelectedEntries) {
          option.preserveSelectedEntries = true;
        }

        this.retrieveItems(option);
      }
    }

    this.debouncedFilters = _.debounce(async (filters) => {
      this.page = 0;
      // preference
      const preference = {
        ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId)
      }
      this.setLocalStoragePreferenceFilters(preference, filters);
      // end preference
      await this.retrieveItems();
    }, 1000);

    this.autoSave = this.createAutoSave(this.designExecuteSave, 1000);

    window.eventBus.$on("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$on("listing:refresh", this.refreshListHandler);
    window.eventBus.$on("entry:updated", this.refreshListHandler);
  },
  async mounted() {
    window.eventBus.$on('open-designer', this.onOpenDesigner)
    this.resizeObserver = new ResizeObserver(_.debounce((entries) => {
      try {
        const entry = entries.find(x => x.target === this.$refs['list-view-container']);
        if (entry) {
          const { width } = entry.contentRect;
          let contentElWidth = width - 32;
          if (contentElWidth > 600) {
            this.contentElWidthStyle = {
              "position": "sticky",
              "left": "1rem",
              "width": `${contentElWidth}px`,
              "max-width": `${contentElWidth}px`,
            }
          } else {
            this.contentElWidthStyle = null;
          }
        }
      } catch (error) {
        console.warn("[DOMException]:ResizeObserver", error);
      }
    }, 100));

    this.resizeObserver.observe(this.$refs['list-view-container']);

    let isCreateModule = false;
    const routeParamsModuleId = this.$route.params.moduleId;
    const routeQueryAction = this.$route.query && this.$route.query.a ? this.$route.query.a.toLowerCase() : null;

    if (routeParamsModuleId) {
      this.moduleId = commons.decodeids(this.$route.params.moduleId);
      this.listingId = this.$route.query.l;
    } else if (this.isAdminUser && this.isDesignMode && routeQueryAction === "create") {
      isCreateModule = true;
    } else {
      let firstOrDefaultMenu = commons.getFirstOrDefaultMenu(clientStore.getters.workspaceMenus);

      if (firstOrDefaultMenu) {
        this.moduleId = firstOrDefaultMenu.moduleId;
        this.listingId = firstOrDefaultMenu.listingId;

        let route = {
          name: this.$route.name,
          params: { moduleId: commons.encodeids(this.moduleId) }
        }

        if (this.listingId) {
          route.query = { l: this.listingId };
        }

        this.$router.push(route);
      }
    }

    if (isCreateModule) {
      this.listViewLoaded = true;
    } else {
      this.loading = true;
      this.listViewLoaded = false;

      if (this.isMiniStudio) {
        this.listingId = "everything";
      }

      // get preference
      const preference = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
      if (preference && preference.pageSize) {
        this.pageSize = preference.pageSize;
      }

      try {
        await clientStore.dispatch("loadWorkspaceModule", {
          moduleId: this.moduleId,
          listId: this.listingId
        });
        // set canvas design, classification users
        canvasDesignStore.dispatch("setUsers", this.workspaceUsers);
        classificationStore.dispatch("setUsers", { users: this.workspaceUsers });
        // module undefined
        if (this.moduleId != clientStore.getters.moduleId) {
          throw {
            isCustom: true,
            message: "Module isn't available. The module you're trying to access is unpublished or have been removed."
          };
        }

        if (this.$route.query.dialog) {
          // to-do
        } else {
          this.initListViewMode({
            code: "l",
            moduleId: this.moduleId,
            listingId: this.listingId
          });

          await this.initialSchema();
          this.listViewLoaded = true;
          this.openForm();

          // if (this.isIndependentView) {
          //   this.listViewLoaded = true;
          // } else {
          //   try {
          //     await this.initialSchema();
          //     this.listViewLoaded = true;
          //   } catch (err) { }
          // }
        }
      } catch (err) {
        console.error(err);
        // to-do error
      } finally {
        this.loading = false;
        await this.$nextTick();

        await new Promise(resolve => setTimeout(resolve, 300));

        this.designReady = true; // 🔓 解锁
      }
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$refs['list-view-container']);
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.autoSave) {
      this.autoSave.flush();
      this.autoSave.cancel();
    }

    // Clean up click outside listeners
    document.removeEventListener('click', this.handleColumnEditClickOutside);

    window.eventBus.$off("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$off("listing:refresh", this.refreshListHandler);
    window.eventBus.$off("entry:updated", this.refreshListHandler);
    clearInterval(this.autoRefreshInterval);
    this.autoRefreshInterval = null;

    if (this.debouncedFilters && this.debouncedFilters.cancel) {
      this.debouncedFilters.cancel();
    }
  },
  async beforeRouteUpdate(to, from, next) {
    next();

    if (to.fullPath !== from.fullPath) {
      if (
        to.params.moduleId !== from.params.moduleId || 
        to.query.l !== from.query.l || 
        to.query.t !== from.query.t
      ) {

        console.info("RouteUpdate:Module or Listing changed", {
          toModuleId: to.params.moduleId,
          fromModuleId: from.params.moduleId,
          toListingId: to.query.l,
          fromListingId: from.query.l,
          toCode: to.query.t,
          fromCode: from.query.t 
        });
      }

      if (!to.query.id && !to.query.a) {
        console.info("RouteUpdate:Close Form", {
          to: to,
          from: from
        })
      } else {
        var toTrigger = false;

        if (to.query.id && (!from.query || to.query.id != from.query.id)) {
          toTrigger = true;
          entryPoint = 1
        } else if (
          to.query.a == "create" && !to.query.dialog &&
          (
            from.query.id ||
            Object.keys(to.query).some(x => x && x.startsWith('t') && x.length > 1)
          )
        ) {
          entryPoint = 2
          toTrigger = true;
        } else if (to.query.a == "create") {
          entryPoint = 3 
          toTrigger = false;
        } else if (to.query.a && (!from.query || to.query.a != from.query.a)) {
          entryPoint = 4
          toTrigger = true;
        } else if (to.query.id && to.query.m !== from.query.m) {
          entryPoint = 5
          toTrigger = true;
        }

        if (toTrigger) {
          let entryId = null;
          let moduleId = null;

          if (to.query.id) {
            const queryDecodeids = commons.queryDecodeids(to.query.id);
            if (queryDecodeids && queryDecodeids.entryId) {
              entryId = queryDecodeids.entryId;
            }
            if (queryDecodeids && queryDecodeids.moduleId) {
              moduleId = queryDecodeids.moduleId;
            }
            if (!moduleId && to.params.moduleId) {
              moduleId = commons.decodeids(to.params.moduleId);
            }
          } else if (to.query.a == "create" && to.params.moduleId) {
            moduleId = commons.decodeids(to.params.moduleId);
          }

          window.eventBus.$emit("list-view-form:open", {
            id: entryId,
            moduleId: moduleId,
            listingId: to.query.l,
            activityId: to.query.a,
            fromUrl: true
          });
        }
      }
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

body {
  overflow: hidden;
}

.layout__content {
  background-color: var(--inistate-color-neutral-50);
}

.layout-position-sticky-left {
  position: sticky;
  left: 0;
}

.fts-list-view-2 {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.list-view__container {
  flex: 1;
  padding: 1rem;
}

.list-view__title {
  @extend .layout-position-sticky-left;
  margin-bottom: 1rem;
}

.list-view__title-wrapper {
  font-weight: var(--inistate-font-weight-bold);
  font-size: var(--inistate-heading-text-20);
  line-height: var(--inistate-heading-text-20--line-height);
}

.list-view__content {
  display: inline-flex;
  flex-direction: column;
  min-width: 100%;
  padding: 1rem;
  border-radius: var(--inistate-rounded-8);
  background-color: #fff;
}

.list-view__content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.list-view__content-header__left {
  flex: 1;  
  align-items: center;
  @include flex-gap($gap: 0.5rem, $display: flex);

  .activities-design-toggle {
    &.fts-btn.fts-btn-outline:not(.disabled) {
      border: 2px dashed var(--fts-gray-300);
      > span {
        font-weight: 400;
      }
    }
  }
  .activities-design-toggle-create {
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--fts-default);
    width: 20px;
    height: 20px;
    border-radius: 99px;
    font-size: 14px;
    
    cursor: pointer;
    &:hover{
      background-color: var(--fts-gray-300); 
      background-color: var(--fts-gray-50);
    }
  }
}

.list-view__content-header__right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.list-view__content-divider {
  margin-bottom: 1rem;
  border-top: 1px solid var(--fts-border);
}

.list-view__content-filter {
  margin-bottom: 1rem;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
}

.list-view__content-pagination {
  margin-top: 1rem;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
}

.list-view__error {
  padding: 1rem;
  text-align: center;
}
.list-view__error-title {
  margin-bottom: 0.375rem;
  font-weight: var(--inistate-font-weight-medium);
  font-size: var(--inistate-paragraph-text-16);
  line-height: var(--inistate-paragraph-text-16--line-height);
  color: var(--fts-danger);
}
.list-view__error-message {
  font-size: var(--inistate-paragraph-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  color: var(--fts-default);
}

.fts-data-table {
  width: 100%;
  border-collapse: collapse;

  .data-table__head {
    .data-table__row {
      background-color: var(--inistate-color-neutral-50);
    }

    .data-table__cell {
      color: var(--inistate-text-secondary-color);
      white-space: nowrap;
      vertical-align: middle;
    }

    .data-table__label-group {
      align-items: center;
    }
  }

  .data-table__body {
    .data-table__row {
      border-bottom: 1px solid var(--fts-border);
    }
  }

  .data-table__row {
    &.data-table__row--selected {
      background-color: rgba(var(--inistate-color-primary-500-rgb), 0.085);
    }

    &:hover {
      .data-table__cell-shortcut {
        display: block;
      }
    }
  }

  .data-table__cell {
    padding: 0.5rem 0.75rem;
    font-weight: normal;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    text-align: left;
    vertical-align: top;
    color: var(--inistate-text-primary-color);
    white-space: nowrap;
    position: relative;

    &.data-table__cell--primary-cell {
      position: relative;
      padding-right: 4.5rem;
    }
    &.data-table__cell--design {
      cursor: pointer;
      &:hover {
        .data-table__cell-design-delete {
          visibility: visible;
        }
      }
    }
    &.data-table__cell--design-control {
      min-width: 200px;
    }
    &.data-table__cell--header {
      &:not(.data-table__cell--design) {
        padding-right: 44px;
      }
    }
    
    .label-text {
     display: inline-block;  // text shrinks to its own width
    }
    .actions-group{  
      padding: 4px 6px;
      display: flex;
      visibility: hidden;
      gap: 8px;
      border-radius: var(--inistate-rounded-4);
      box-shadow: var(--fts-elevation-1);
      background-color: var(--fts-gray-25);
      color: var(--fts-default);
      margin-left: 10px;
      
      .actions-group-action{
        display: flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        line-height: 1;
      }
    }

    &:hover{ 
      .actions-group{
          visibility: visible; 
      }
    }
  }

  .data-table__cell-design-delete {
    margin-top: -4px;
    margin-bottom: -4px;
    margin-left: 4px;
    visibility: hidden;
  }

  .data-table__cell-action {
    min-width: 50px;
    text-align: center;
  }

  .data-table__cell-shortcut {
    @include user-select-none;
    display: none;
    position: absolute;
    top: 5px;
    right: 0.25rem;
    box-shadow: var(--fts-elevation-2);
    border-radius: 0.25rem;
    background: var(--fts-popover-alt-background);

    >.fts-btn {
      padding: 0.25rem 0.375rem;
      min-height: 0;
      height: auto;
      font-weight: var(--inistate-font-weight-light);

      i {
        margin-right: 0.25rem;
      }
    }
  }

  .data-table__label-group {
    display: flex;
    align-items: flex-start;
  }

  .data-table__label {
    display: flex;
    flex-direction: column;
    margin-left: 1rem;
  }

  .data-table__status {
    >div {
      font-size: var(--inistate-label-text-14);
      line-height: var(--inistate-paragraph-text-14--line-height);
    }
  }
}

// design
.lv-design__header {
  @extend .layout-position-sticky-left;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  border-radius: var(--inistate-rounded-8);
  background-color: #fff;
}

.lv-design__header-wrapper {
  flex: 1;
  min-width: 0;
}

.lv-design__header-info {
  margin-top: 5px;
  margin-bottom: 5px;

  >*:not(:last-child) {
    margin-bottom: 2px;
  }
}

.lv-design__module-emoji {
  flex-shrink: 0;
  height: 60px;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  border-radius: var(--inistate-rounded-8);
  background-color: var(--inistate-color-primary-50);
}

.lv-design__module-name {
  margin-bottom: 4px;
  font-weight: var(--inistate-font-weight-bold);
  font-size: var(--inistate-heading-text-18);
  line-height: var(--inistate-heading-text-18--line-height);
}

.lv-design__module-description {
  font-size: var(--inistate-label-text-16);
  line-height: var(--inistate-label-text-16--line-height);
}

.lv-design__error-message {
  color: var(--fts-danger);
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-label-text-14--line-height);
}

// Information Sort Dropdown
.information-sort-dropdown {
  min-width: 280px;
  max-width: 320px;
  max-height: 450px;
  overflow-y: auto;
}

.information-sort-menu {
  padding: 0.25rem 0;
}

.information-sort-header {
  padding: 0.5rem 0.75rem;
  font-weight: var(--inistate-font-weight-bold);
  font-size: var(--inistate-label-text-14);
  color: var(--inistate-text-secondary-color);
  border-bottom: 1px solid var(--fts-border);
  margin-bottom: 0.25rem;
}

.information-sort-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin: 0 0.25rem;
  border-radius: 0.25rem;
  cursor: default;

  &:hover {
    background-color: rgba(var(--fts-gray-100-rgb), 0.75);

    .information-sort-handle {
      visibility: visible;
    }
  }
}

.information-sort-handle {
  color: var(--fts-default);
  font-size: 0.75rem;
  visibility: hidden;
  cursor: grab;
}

.information-sort-icon {
  color: var(--inistate-text-secondary-color);
  font-size: 0.875rem;
  width: 1rem;
  text-align: center;
}

.information-sort-name {
  flex: 1;
  font-size: var(--inistate-label-text-14);
  color: var(--inistate-text-primary-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.information-sort-item.ghost {
  opacity: 0.5;
  background: var(--fts-background-secondary);
}

.add-column-trigger {
  cursor: pointer;
  color: var(--inistate-text-secondary-color);

  &:hover {
    color: var(--inistate-text-primary-color);
  }

  // Align the plus icon with other column icons
  >div:first-child {
    width: 1rem;
    text-align: center;
  }
}

.add-column-edit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.add-column-input {
  flex: 1;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--fts-border);
  border-radius: var(--inistate-rounded-4);
  font-size: var(--inistate-label-text-14);
  background: var(--fts-background);
  color: var(--inistate-text-primary-color);
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: var(--inistate-color-primary);
  }

  &::placeholder {
    color: var(--fts-disabled);
  }
}

.add-column-type-trigger {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--fts-border);
  border-radius: var(--inistate-rounded-4);
  cursor: pointer;
  font-size: var(--inistate-label-text-14);
  color: var(--inistate-text-secondary-color);
  background: var(--fts-background);
  white-space: nowrap;

  &:hover {
    border-color: var(--inistate-color-primary);
    color: var(--inistate-text-primary-color);
  }
}
</style>